import axios from 'axios';

import { handleResponseError } from '@/Helpers.js';
import Group from '@/models/Group.js';
import Model from '@/models/Model.js';
import TermScheduleGroup from '@/models/term-schedule/TermScheduleGroup.js';
import TermSchedulePeriod from '@/models/term-schedule/TermSchedulePeriod.js';

export default class TermSchedule extends Model {
    static entity = 'term_schedules';

    static modelName = 'TermSchedule';

    static fields() {
        return {
            id: this.number(null),
            term_schedule_template_id: this.number(null),
            external_id: this.attr(null),
            group_id: this.number(null),
            title: this.string(null),
            description: this.attr(null).nullable(),
            status: this.string('DRAFT'),
            term_total_periods: this.number(null).nullable(),
            term_total_breaks: this.number(null).nullable(),
            term_total_weeks: this.number(null).nullable(),
            term_total_courses: this.number(null).nullable(),
            term_total_credits: this.number(null).nullable(),
            term_start_date: this.attr(null),
            term_end_date: this.attr(null),

            ...super.fields(),

            periods: this.hasMany(TermSchedulePeriod, 'term_schedule_id', 'id'),
            launched_groups: this.belongsToMany(Group, TermScheduleGroup, 'term_schedule_id', 'group_id'),
        };
    }

    // Defaults
    get modelName() {
        return this.modelName;
    }

    static searchable() {
        return ['title'];
    }

    // API - GET
    static async fetch(id) {
        const endpoint = `/api/term-schedules/${id}`;
        try {
            const { response } = await this.api().get(endpoint);
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async manualFetch(id) {
        const endpoint = `/api/term-schedules/${id}`;
        try {
            const response = await axios.get(endpoint);
            await this.insertOrUpdate({
                data: response.data.data,
            });
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async fetchGroupTermSchedules(groupId) {
        const endpoint = `/api/groups/${groupId}/term-schedules`;
        try {
            const { response } = await this.api().get(endpoint);
            return response.data.data;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async getTermByIdWithPeriods(id) {
        await this.fetch(id);
        return this.query().with('periods').whereId(id).first();
    }

    // API - PUT
    static async sync(id, data) {
        const endpoint = `/api/term-schedules/${id}`;
        try {
            const { response } = await this.api().put(endpoint, data);

            if (response.status === 200) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    get term_periods() {
        return this.periods.filter(period => period.type === 'period');
    }

    get term_courses() {
        return this.term_periods.reduce((acc, period) => acc.concat(period.courses), []);
    }

    get expected_instruction_weeks() {
        return this.term_periods.reduce((acc, period) => acc + period.weeks, 0);
    }

    get courses_with_no_breaks() {
        return this.term_courses.filter(course => course.type === 'course');
    }

    get calendar_weeks() {
        return this.periods.reduce((acc, period) => acc + period.calculated_calendar_weeks, 0);
    }
}
